import React, { useState, useEffect } from 'react';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import { Player, PlayerAttributes, Position } from '../../types';
import { calculatePositionRatings, isValidRating } from '../../utils/calculations';
import { usePlayers } from '../../contexts/PlayerContext';
import { PlayerCardUpload } from './PlayerCardUpload';

interface PlayerEditModalProps {
  player: Player;
  isOpen: boolean;
  onClose: () => void;
}

const ATTRIBUTE_DEFINITIONS: Record<keyof PlayerAttributes, string> = {
  // Technical (5)
  passing: 'Accuracy and quality of passes to teammates. Includes short and long passes.',
  first_touch: 'Ability to control the ball cleanly when receiving it. Good first touch sets up the next action.',
  dribbling: 'Skill at running with the ball under control and ability to beat opponents 1v1.',
  finishing: 'Ability to score goals when presented with chances. Includes accuracy and power of shots.',
  vision: 'Seeing passes and opportunities others miss. Key for playmakers and creative players.', // NEW v2.0
  // Physical (6)
  pace: 'Top running speed. How fast a player can sprint.',
  stamina: 'Fitness and ability to maintain performance throughout the entire match without tiring.',
  agility: 'Ability to change direction quickly, turn sharply, and maneuver in tight spaces.',
  acceleration: 'How quickly a player reaches top speed from a standing start or slow jog.',
  strength: 'Physical power in challenges, shielding the ball, and winning aerial duels.', // NEW v2.0
  balance: 'Stability and body control when moving, turning, or being tackled.', // NEW v2.0
  // Mental (6)
  decision_making: 'Choosing the right option in any situation - when to pass, shoot, dribble, or tackle.',
  work_rate: 'Willingness to put in effort, track back, press opponents, and contribute throughout.',
  composure: 'Staying calm under pressure, making good decisions in high-stakes moments.',
  anticipation: 'Reading the game and predicting what will happen next to get into position early.',
  leadership: 'Ability to organize teammates, communicate effectively, and inspire those around you.',
  teamwork: 'Playing for the team, making the right decisions for the collective rather than individual glory.',
  // Defensive (3)
  tackling: 'Ability to win the ball from opponents through challenges and interceptions.',
  marking: 'Ability to track and stay close to opponents, denying them space and time.',
  defensive_awareness: 'Reading danger, positioning to intercept, and defensive decision-making.', // NEW v2.0
  // Attacking (1)
  off_the_ball_movement: 'Intelligent runs, finding space, and movement to create opportunities.', // NEW v2.0
  // Specialist (1)
  goalkeeping: 'Overall ability in goal including shot-stopping, positioning, reactions, and distribution.',
  // Deprecated (backward compat)
  positioning: '[DEPRECATED] Use Defensive Awareness instead.',
  off_the_ball: '[DEPRECATED] Use Off the Ball Movement instead.',
  balance_strength: '[DEPRECATED] Split into Strength and Balance.',
  // Manual overrides
  manual_fwd_rating: 'Manual override for forward rating (0-10). Blended 50/50 with calculated rating.',
  manual_mid_rating: 'Manual override for midfielder rating (0-10). Blended 50/50 with calculated rating.',
  manual_def_rating: 'Manual override for defender rating (0-10). Blended 50/50 with calculated rating.',
  manual_gk_rating: 'Manual override for goalkeeper rating (0-10). Blended 50/50 with calculated rating.',
};

export function PlayerEditModal({ player, isOpen, onClose }: PlayerEditModalProps) {
  const { updatePlayer } = usePlayers();
  const [name, setName] = useState(player.name);
  const [favoritePosition, setFavoritePosition] = useState<Position>(
    player.favorite_position
  );
  const [attributes, setAttributes] = useState<PlayerAttributes>(
    player.attributes || {
      // Technical (5)
      passing: null,
      first_touch: null,
      dribbling: null,
      finishing: null,
      vision: null, // v2.0
      // Physical (6)
      pace: null,
      stamina: null,
      agility: null,
      acceleration: null,
      strength: null, // v2.0
      balance: null, // v2.0
      // Mental (6)
      decision_making: null,
      work_rate: null,
      composure: null,
      anticipation: null,
      leadership: null,
      teamwork: null,
      // Defensive (3)
      tackling: null,
      marking: null,
      defensive_awareness: null, // v2.0
      // Attacking (1)
      off_the_ball_movement: null, // v2.0
      // Specialist (1)
      goalkeeping: null,
      // Deprecated (backward compat)
      positioning: null,
      off_the_ball: null,
      balance_strength: null,
      // Manual overrides
      manual_fwd_rating: null,
      manual_mid_rating: null,
      manual_def_rating: null,
      manual_gk_rating: null,
    }
  );
  const [calculatedRatings, setCalculatedRatings] = useState(player.calculated);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Recalculate ratings when attributes change
  useEffect(() => {
    const newRatings = calculatePositionRatings(attributes);
    setCalculatedRatings(newRatings);
  }, [attributes]);

  // Auto-save with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (isOpen) {
        handleSave();
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [name, favoritePosition, attributes]);

  const handleAttributeChange = (key: keyof PlayerAttributes, value: string) => {
    if (value === '') {
      setAttributes((prev) => ({ ...prev, [key]: null }));
      return;
    }

    if (!isValidRating(value)) {
      return; // Don't update if invalid
    }

    setAttributes((prev) => ({ ...prev, [key]: parseInt(value) }));
  };

  const handleSave = async () => {
    try {
      setError('');
      setSaving(true);

      await updatePlayer(player.id, {
        name: name.trim(),
        favorite_position: favoritePosition,
        attributes,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
      console.error('Save error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    handleSave();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Edit Player" size="lg">
      <div className="space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Basic Info */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Player Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Favorite Position
            </label>
            <select
              value={favoritePosition || ''}
              onChange={(e) =>
                setFavoritePosition(e.target.value === '' ? null : (e.target.value as Position))
              }
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Auto (Best Position)</option>
              <option value="FWD">Forward (FWD)</option>
              <option value="MID">Midfielder (MID)</option>
              <option value="DEF">Defender (DEF)</option>
              <option value="GK">Goalkeeper (GK)</option>
            </select>
          </div>
        </div>

        {/* Player Card Upload */}
        <div>
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
            FIFA-Style Card (For Video Reveals)
          </h3>
          <PlayerCardUpload
            playerId={player.id}
            playerName={player.name}
            hasCard={player.has_card}
            onUploadSuccess={() => {
              // Refresh player data after upload
              window.location.reload();
            }}
          />
        </div>

        {/* Calculated Ratings Display */}
        {calculatedRatings && (
          <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Calculated Ratings
            </h3>
            <div className="grid grid-cols-5 gap-3 text-sm">
              <div>
                <div className="text-gray-600 dark:text-gray-400">FWD</div>
                <div className="font-semibold text-lg dark:text-gray-200">{calculatedRatings.fwd_rating.toFixed(1)}</div>
              </div>
              <div>
                <div className="text-gray-600 dark:text-gray-400">MID</div>
                <div className="font-semibold text-lg dark:text-gray-200">{calculatedRatings.mid_rating.toFixed(1)}</div>
              </div>
              <div>
                <div className="text-gray-600 dark:text-gray-400">DEF</div>
                <div className="font-semibold text-lg dark:text-gray-200">{calculatedRatings.def_rating.toFixed(1)}</div>
              </div>
              <div>
                <div className="text-gray-600 dark:text-gray-400">GK</div>
                <div className="font-semibold text-lg dark:text-gray-200">{calculatedRatings.gk_rating.toFixed(1)}</div>
              </div>
              <div className="border-l dark:border-gray-600 pl-3">
                <div className="text-gray-600 dark:text-gray-400">Overall</div>
                <div className="font-bold text-xl text-primary-600 dark:text-primary-400">
                  {calculatedRatings.overall_rating.toFixed(1)}
                </div>
              </div>
            </div>
            <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
              Best Position: <span className="font-semibold dark:text-gray-300">{calculatedRatings.best_position}</span>
            </div>
          </div>
        )}

        {/* Attributes - Organized by Category */}
        <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
          {/* Technical Attributes (5) */}
          <AttributeSection title="Technical Attributes (5)">
            <AttributeInput
              label="Passing"
              attributeKey="passing"
              value={attributes.passing}
              onChange={(v) => handleAttributeChange('passing', v)}
            />
            <AttributeInput
              label="First Touch"
              attributeKey="first_touch"
              value={attributes.first_touch}
              onChange={(v) => handleAttributeChange('first_touch', v)}
            />
            <AttributeInput
              label="Dribbling"
              attributeKey="dribbling"
              value={attributes.dribbling}
              onChange={(v) => handleAttributeChange('dribbling', v)}
            />
            <AttributeInput
              label="Finishing"
              attributeKey="finishing"
              value={attributes.finishing}
              onChange={(v) => handleAttributeChange('finishing', v)}
            />
            <AttributeInput
              label="Vision ✨"
              attributeKey="vision"
              value={attributes.vision}
              onChange={(v) => handleAttributeChange('vision', v)}
            />
          </AttributeSection>

          {/* Physical Attributes (6) */}
          <AttributeSection title="Physical Attributes (6)">
            <AttributeInput
              label="Pace"
              attributeKey="pace"
              value={attributes.pace}
              onChange={(v) => handleAttributeChange('pace', v)}
            />
            <AttributeInput
              label="Stamina"
              attributeKey="stamina"
              value={attributes.stamina}
              onChange={(v) => handleAttributeChange('stamina', v)}
            />
            <AttributeInput
              label="Agility"
              attributeKey="agility"
              value={attributes.agility}
              onChange={(v) => handleAttributeChange('agility', v)}
            />
            <AttributeInput
              label="Acceleration"
              attributeKey="acceleration"
              value={attributes.acceleration}
              onChange={(v) => handleAttributeChange('acceleration', v)}
            />
            <AttributeInput
              label="Strength ✨"
              attributeKey="strength"
              value={attributes.strength}
              onChange={(v) => handleAttributeChange('strength', v)}
            />
            <AttributeInput
              label="Balance ✨"
              attributeKey="balance"
              value={attributes.balance}
              onChange={(v) => handleAttributeChange('balance', v)}
            />
          </AttributeSection>

          {/* Mental Attributes (6) */}
          <AttributeSection title="Mental Attributes (6)">
            <AttributeInput
              label="Decision Making"
              attributeKey="decision_making"
              value={attributes.decision_making}
              onChange={(v) => handleAttributeChange('decision_making', v)}
            />
            <AttributeInput
              label="Work Rate"
              attributeKey="work_rate"
              value={attributes.work_rate}
              onChange={(v) => handleAttributeChange('work_rate', v)}
            />
            <AttributeInput
              label="Composure"
              attributeKey="composure"
              value={attributes.composure}
              onChange={(v) => handleAttributeChange('composure', v)}
            />
            <AttributeInput
              label="Anticipation"
              attributeKey="anticipation"
              value={attributes.anticipation}
              onChange={(v) => handleAttributeChange('anticipation', v)}
            />
            <AttributeInput
              label="Leadership"
              attributeKey="leadership"
              value={attributes.leadership}
              onChange={(v) => handleAttributeChange('leadership', v)}
            />
            <AttributeInput
              label="Teamwork"
              attributeKey="teamwork"
              value={attributes.teamwork}
              onChange={(v) => handleAttributeChange('teamwork', v)}
            />
          </AttributeSection>

          {/* Defensive Attributes (3) */}
          <AttributeSection title="Defensive Attributes (3)">
            <AttributeInput
              label="Tackling"
              attributeKey="tackling"
              value={attributes.tackling}
              onChange={(v) => handleAttributeChange('tackling', v)}
            />
            <AttributeInput
              label="Marking"
              attributeKey="marking"
              value={attributes.marking}
              onChange={(v) => handleAttributeChange('marking', v)}
            />
            <AttributeInput
              label="Defensive Awareness ✨"
              attributeKey="defensive_awareness"
              value={attributes.defensive_awareness}
              onChange={(v) => handleAttributeChange('defensive_awareness', v)}
            />
          </AttributeSection>

          {/* Attacking Attributes (1) */}
          <AttributeSection title="Attacking Attributes (1)">
            <AttributeInput
              label="Off the Ball Movement ✨"
              attributeKey="off_the_ball_movement"
              value={attributes.off_the_ball_movement}
              onChange={(v) => handleAttributeChange('off_the_ball_movement', v)}
            />
          </AttributeSection>

          {/* Goalkeeping (1) */}
          <AttributeSection title="Goalkeeping (1)">
            <AttributeInput
              label="Goalkeeping"
              attributeKey="goalkeeping"
              value={attributes.goalkeeping}
              onChange={(v) => handleAttributeChange('goalkeeping', v)}
            />
          </AttributeSection>
        </div>

        {/* Actions */}
        <div className="flex justify-between items-center pt-4 border-t dark:border-gray-700">
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {saving ? 'Saving...' : 'Auto-saved'}
          </div>
          <div className="space-x-3">
            <Button variant="secondary" onClick={handleClose}>
              Close
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

// Helper Components
function AttributeSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 pb-1 border-b dark:border-gray-700">
        {title}
      </h3>
      <div className="grid grid-cols-2 gap-3">{children}</div>
    </div>
  );
}

function AttributeInput({
  label,
  attributeKey,
  value,
  onChange,
}: {
  label: string;
  attributeKey: keyof PlayerAttributes;
  value: number | null;
  onChange: (value: string) => void;
}) {
  const definition = ATTRIBUTE_DEFINITIONS[attributeKey];

  return (
    <div className="group relative">
      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 cursor-help">
        {label}
        <span className="ml-1 text-gray-400 dark:text-gray-500 text-[10px]">ⓘ</span>
      </label>
      <input
        type="number"
        min="1"
        max="10"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder="1-10"
        className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
        title={definition}
      />
      {/* Tooltip */}
      <div className="invisible group-hover:visible absolute z-10 w-64 p-2 mt-1 text-xs text-white bg-gray-900 dark:bg-gray-800 rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
        {definition}
        <div className="absolute -top-1 left-4 w-2 h-2 bg-gray-900 dark:bg-gray-800 transform rotate-45"></div>
      </div>
    </div>
  );
}
